<!--
  告警配置 · 告警文案：按告警类型（1紧急 2恢复 3警告）展示接口返回的标题与内容
  · 编辑保存：文案保存接口（alertType / title / content）
  · 测试推送：选择接收人后调测试推送接口（alertType / recipientId）
-->
<template>
  <div class="template-pane">
    <div class="pane-head">
      <div class="ph-tip">
        变量以 {名称} 形式插入，推送时自动替换
        <span class="tip-pt">Variáveis no formato {nome}, substituídas automaticamente no envio</span>
      </div>
      <div class="ph-actions">
        <el-button
          size="small" type="primary" icon="el-icon-plus"
          @click="handleAdd"
        >
          <bi-label zh="新增文案" pt="Novo Texto" inline />
        </el-button>
      </div>
    </div>

    <div class="template-list">
      <div v-for="(t, i) in rows" :key="t.alertType" class="template-item">
        <div class="t-num">{{ pad2(i + 1) }}</div>
        <div class="t-body">
          <div class="t-label">
            <span class="t-type" :class="typeCls(t.alertType)">
              {{ typeZh(t.alertType) }}
              <span class="t-type-pt">/ {{ typePt(t.alertType) }}</span>
            </span>
          </div>
          <div class="t-line">
            <div class="t-k">标题<span class="t-k-pt"> / Título</span></div>
            <div class="t-v">{{ t.title || '—' }}</div>
          </div>
          <div class="t-line">
            <div class="t-k">内容<span class="t-k-pt"> / Conteúdo</span></div>
            <div class="t-v t-v-pre">{{ t.content || '—' }}</div>
          </div>

          <div class="t-vars">
            <div class="vars-title">可用变量<span class="vars-title-pt"> / Variáveis</span></div>
            <div v-for="v in varsOf(t.alertType)" :key="v" class="var">
              <span>{</span>{{ v }}<span>}</span>
            </div>
          </div>
        </div>
        <div class="t-actions">
          <el-button size="small" @click="handleEdit(t)">
            <bi-label zh="编辑文案" pt="Editar Texto" inline />
          </el-button>
          <el-button size="small" @click="openTestPush(t)">
            <bi-label zh="测试推送" pt="Testar Envio" inline />
          </el-button>
        </div>
      </div>

      <div v-if="!rows.length && !loading" class="list-empty">
        接口暂无告警文案数据
        <span class="empty-pt">/ Nenhum texto de alerta retornado pela API</span>
      </div>
    </div>

    <!-- 新增 / 编辑文案：新增时需选择告警类型，其余字段一致 -->
    <el-dialog
      :visible.sync="dialogVisible"
      width="640px"
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div slot="title" class="td-title">
        <span>{{ isAdd ? '新增文案' : `编辑文案 · ${typeZh(current.alertType)}` }}</span>
        <span class="td-pt">/ {{ isAdd ? 'Novo Texto' : `Editar Texto · ${typePt(current.alertType)}` }}</span>
      </div>

      <el-form
        v-if="dialogVisible" ref="formRef" :model="form"
        :rules="rules"
        label-position="top"
        size="small"
      >
        <el-form-item v-if="isAdd" prop="alertType">
          <template slot="label">
            <bi-label zh="告警类型" pt="Tipo de Alerta" inline />
          </template>
          <el-select
            v-model="form.alertType" style="width: 100%"
            placeholder="请选择告警类型 / Selecione o tipo de alerta"
          >
            <el-option
              v-for="t in typeOptions" :key="t.value" :label="t.label"
              :value="t.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item prop="title">
          <template slot="label">
            <bi-label zh="标题" pt="Título" inline />
          </template>
          <el-input v-model="form.title" maxlength="60" show-word-limit />
        </el-form-item>
        <el-form-item prop="content">
          <template slot="label">
            <bi-label zh="内容" pt="Conteúdo" inline />
          </template>
          <el-input v-model="form.content" type="textarea" :rows="4" />
        </el-form-item>
        <div class="td-hint">
          变量以 {名称} 形式插入，推送时自动替换：
          <span class="td-vars">
            <span v-for="v in varsOf(isAdd ? form.alertType : current.alertType)" :key="v" class="var">
              <span>{</span>{{ v }}<span>}</span>
            </span>
          </span>
          <span class="td-hint-pt">/ Variáveis no formato {nome}, substituídas automaticamente no envio</span>
        </div>
      </el-form>

      <div slot="footer" class="td-footer">
        <el-button size="small" @click="dialogVisible = false">
          <bi-label zh="取消" pt="Cancelar" inline />
        </el-button>
        <el-button
          size="small" type="primary" :loading="saving"
          @click="handleSave"
        >
          <bi-label zh="保存" pt="Salvar" inline />
        </el-button>
      </div>
    </el-dialog>

    <!-- 测试推送：选择接收人后确认发送 -->
    <el-dialog
      :visible.sync="testVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div slot="title" class="td-title">
        <span>测试推送 · {{ typeZh(testTarget.alertType) }}</span>
        <span class="td-pt">/ Testar Envio · {{ typePt(testTarget.alertType) }}</span>
      </div>

      <el-form label-position="top" size="small">
        <el-form-item>
          <template slot="label">
            <bi-label zh="接收人" pt="Destinatário" inline />
          </template>
          <el-select v-model="testRecipientId" placeholder="请选择接收人 / Selecione o destinatário" style="width: 100%">
            <el-option
              v-for="r in recipients" :key="r.id" :label="r.name"
              :value="r.id"
            />
          </el-select>
          <div class="td-hint">
            将向所选飞书群发送一条示例告警，发送成功后该接收人连通性恢复为「正常」
            <span class="td-hint-pt">Enviará um alerta de exemplo ao grupo selecionado; após o envio a saúde volta a "Normal"</span>
          </div>
        </el-form-item>
      </el-form>

      <div slot="footer" class="td-footer">
        <el-button size="small" @click="testVisible = false">
          <bi-label zh="取消" pt="Cancelar" inline />
        </el-button>
        <el-button
          size="small" type="primary" :loading="pushing"
          :disabled="!testRecipientId"
          @click="handleTestPush"
        >
          <bi-label zh="发送测试" pt="Enviar Teste" inline />
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import BiLabel from './BiLabel';
import { ALERT_TYPE_LABEL } from '../mock';
import {
  getInvoiceSubjectStatusMonitorTemplateList,
  getInvoiceSubjectStatusMonitorTemplateSave,
  getInvoiceSubjectStatusMonitorTemplateTestPush,
  getInvoiceSubjectStatusMonitorRecipientList
} from '@/api/invoiceCenter';

/** 各告警类型可用变量（展示提示，推送时替换） */
const VARS_BY_TYPE = {
  1: ['UF', 'IE', '公司名称', '旧状态', '新状态'],
  2: ['UF', 'IE', '公司名称', '新状态'],
  3: ['UF', 'IE', '公司名称', '次数']
};

export default {
  name: 'TemplatePane',
  components: { BiLabel },
  data() {
    return {
      // 文案列表：字段同接口（id / alertType / title / content）
      rows: [],
      // 接收人选项：测试推送时选择
      recipients: [],
      loading: false,
      saving: false,
      pushing: false,
      dialogVisible: false,
      // 弹窗模式：add 新增（需选告警类型）/ edit 编辑（类型固定）
      dialogMode: 'add',
      current: { alertType: 1, title: '', content: '' },
      form: { alertType: 1, title: '', content: '' },
      rules: {
        alertType: [{ required: true, message: '请选择告警类型 / Selecione o tipo de alerta', trigger: 'change' }],
        title: [{ required: true, message: '标题必填 / Título obrigatório', trigger: 'blur' }],
        content: [{ required: true, message: '内容必填 / Conteúdo obrigatório', trigger: 'blur' }]
      },
      testVisible: false,
      testTarget: { alertType: 1 },
      testRecipientId: null
    };
  },
  computed: {
    /** 弹窗是否新增模式 */
    isAdd() {
      return this.dialogMode === 'add';
    },
    /** 新增时的告警类型选项：已存在文案的类型标注「已存在」，保存后将覆盖 */
    typeOptions() {
      return Object.keys(ALERT_TYPE_LABEL).map(k => {
        const v = Number(k);
        const exist = this.rows.some(r => Number(r.alertType) === v);
        return {
          value: v,
          label: `${ALERT_TYPE_LABEL[v].zh} / ${ALERT_TYPE_LABEL[v].pt}${exist ? '（已存在 / Já existe）' : ''}`
        };
      });
    }
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    // ============ 接口 ============
    /** 拉取文案列表：失败只提示，列表保持空态 */
    async fetchList() {
      this.loading = true;
      try {
        const res = await getInvoiceSubjectStatusMonitorTemplateList();
        const list = Array.isArray(res && res.data) ? res.data : [];
        // 按告警类型排序：1 紧急 → 2 恢复 → 3 警告
        this.rows = list.slice().sort((a, b) => Number(a.alertType) - Number(b.alertType));
      } catch (e) {
        this.rows = [];
        this.$message.error('告警文案接口请求失败，请稍后重试 / Falha ao consultar textos');
        console.error('[告警文案] 列表接口请求失败', e);
      } finally {
        this.loading = false;
      }
    },
    /** 拉取接收人选项（测试推送用） */
    async fetchRecipients() {
      try {
        const res = await getInvoiceSubjectStatusMonitorRecipientList();
        this.recipients = Array.isArray(res && res.data) ? res.data : [];
      } catch (e) {
        this.recipients = [];
        this.$message.error('接收人列表接口请求失败，请稍后重试 / Falha ao consultar destinatários');
        console.error('[告警文案] 接收人列表接口请求失败', e);
      }
    },
    /**
     * 统一封装接口调用：失败只打日志并返回 null，由调用方提示
     * @param {string} label 接口名称（仅用于日志）
     * @param {Function} fn 真正的请求函数
     * @returns {Promise<object|null>} 成功返回 res，失败返回 null
     */
    async callApi(label, fn) {
      try {
        return await fn();
      } catch (e) {
        console.error(`[告警文案] ${label} 请求失败`, e);
        return null;
      }
    },

    // ============ 交互 ============
    /** 打开编辑弹窗，回填当前类型的标题与内容（类型固定不可改） */
    handleEdit(t) {
      this.dialogMode = 'edit';
      this.current = t;
      this.form = { alertType: Number(t.alertType), title: t.title || '', content: t.content || '' };
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate());
    },
    /** 打开新增弹窗：默认选中第一个尚未配置文案的告警类型 */
    handleAdd() {
      this.dialogMode = 'add';
      this.current = { alertType: 1, title: '', content: '' };
      const free = this.typeOptions.find(o => o.label.indexOf('已存在') === -1);
      this.form = { alertType: free ? free.value : 1, title: '', content: '' };
      this.dialogVisible = true;
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate());
    },
    /** 保存文案：新增 / 编辑共用保存接口，成功后刷新列表 */
    async handleSave() {
      const valid = await new Promise(resolve => this.$refs.formRef.validate(v => resolve(v)));
      if (!valid) return;
      const payload = {
        alertType: this.isAdd ? this.form.alertType : this.current.alertType,
        title: String(this.form.title || '').trim(),
        content: String(this.form.content || '').trim()
      };
      this.saving = true;
      const res = await this.callApi('save', () => getInvoiceSubjectStatusMonitorTemplateSave(payload));
      this.saving = false;
      if (!res) {
        this.$message.error('文案保存失败，请稍后重试 / Falha ao salvar o texto');
        return;
      }
      this.dialogVisible = false;
      await this.fetchList();
      this.$message.success(this.isAdd
        ? `已新增「${this.typeZh(payload.alertType)}」文案 / Texto "${this.typePt(payload.alertType)}" criado`
        : `已保存「${this.typeZh(payload.alertType)}」文案 / Texto "${this.typePt(payload.alertType)}" salvo`);
    },
    /** 打开测试推送弹窗：先拉接收人列表 */
    async openTestPush(t) {
      this.testTarget = t;
      this.testRecipientId = null;
      this.testVisible = true;
      if (!this.recipients.length) await this.fetchRecipients();
    },
    /** 发送测试推送 */
    async handleTestPush() {
      const payload = {
        alertType: this.testTarget.alertType,
        recipientId: this.testRecipientId
      };
      this.pushing = true;
      const res = await this.callApi('push', () => getInvoiceSubjectStatusMonitorTemplateTestPush(payload));
      this.pushing = false;
      if (!res) {
        this.$message.error('测试推送失败，请检查接收人 webhook 配置 / Falha no envio de teste');
        return;
      }
      this.testVisible = false;
      this.$message.success('测试消息已发送 / Mensagem de teste enviada');
    },

    // ============ 展示工具 ============
    /** 序号补零：1 → 01 */
    pad2(n) {
      return String(n).padStart(2, '0');
    },
    /** 该类型可用变量 */
    varsOf(alertType) {
      return VARS_BY_TYPE[alertType] || VARS_BY_TYPE[1];
    },
    /** 告警类型中文 */
    typeZh(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].zh) || `类型 ${v}`;
    },
    /** 告警类型葡语 */
    typePt(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].pt) || v;
    },
    /** 告警类型标签配色 */
    typeCls(v) {
      return (ALERT_TYPE_LABEL[v] && ALERT_TYPE_LABEL[v].cls) || 'tag-gray';
    }
  }
};
</script>

<style scoped lang="scss">
.pane-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.ph-tip {
  font-size: 12px;
  color: #86909c;
}
.ph-tip .tip-pt {
  display: block;
}
.ph-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.template-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.template-item {
  display: flex;
  align-items: flex-start;
  padding: 14px 16px;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  background: #fff;
}
.template-item .t-num {
  font-weight: 700;
  color: #00a551;
  margin-right: 12px;
  min-width: 22px;
  font-size: 13px;
}
.template-item .t-body {
  flex: 1;
  min-width: 0;
}
.t-label {
  margin-bottom: 8px;
}
.t-type {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  line-height: 18px;
}
.t-type .t-type-pt {
  font-size: 12px;
  font-weight: 400;
}
.tag-err { background: #ffecec; color: #f53f3f; }
.tag-ok { background: #e8f7e9; color: #00b42a; }
.tag-warn { background: #fff3e0; color: #ff7d00; }
.tag-gray { background: #f2f3f5; color: #4e5969; }

.t-line {
  gap: 8px;
  margin-bottom: 6px;
}
.t-k {
  font-size: 12px;
  color: #86909c;
}
.t-k .t-k-pt {
  font-size: 12px;
}
.t-v {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #1d2129;
  padding: 8px 10px;
  background: #fafbfc;
  border-radius: 4px;
  line-height: 1.6;
  word-break: break-word;
}
.t-v-pre {
  white-space: pre-wrap;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
}

.t-vars {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  align-items: center;
}
.vars-title {
  font-size: 12px;
  color: #86909c;
  margin-right: 2px;
}
.vars-title .vars-title-pt {
  font-size: 12px;
}
.t-vars .var {
  font-size: 12px;
  padding: 2px 8px;
  background: #e8f7f0;
  color: #00a551;
  border-radius: 10px;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
}
.t-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 12px;
}

.list-empty {
  padding: 24px 0;
  text-align: center;
  font-size: 12px;
  color: #86909c;
}
.list-empty .empty-pt {
  display: block;
  margin-top: 2px;
}

.td-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  display: flex;
  flex-direction: column;
}
.td-title .td-pt {
  font-size: 12px;
  font-weight: 400;
  color: #86909c;
}
.td-hint {
  font-size: 12px;
  color: #86909c;
  line-height: 1.6;
}
.td-hint .td-hint-pt {
  display: block;
}
.td-vars {
  display: inline-flex;
  gap: 6px;
  margin-left: 4px;
}
.td-hint .var {
  font-size: 12px;
  padding: 2px 8px;
  background: #e8f7f0;
  color: #00a551;
  border-radius: 10px;
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
}
</style>
